import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { FeatureCarouselNavigation } from './FeatureCarouselNavigation'
import { type FeatureSlide, featureSlides } from './slides'
import { useFeatureCarousel } from './use-feature-carousel'

function TourHeading({ progress }: { progress: string }) {
  const { t } = useTranslation()
  return (
    <p className="shrink-0 bg-muted/30 px-6 pb-1 pt-4 text-2xs font-medium tracking-wide text-muted-foreground sm:px-8">
      {t('whatsNew.title')}{' '}
      <span className="mx-2" aria-hidden>
        ·
      </span>{' '}
      {progress}
    </p>
  )
}

function SlideBody({ slide }: { slide: FeatureSlide }) {
  const { t } = useTranslation()
  return (
    <div className="px-6 pt-4 sm:px-8 sm:pt-8">
      <div className="mb-3 flex items-center gap-2.5 text-sm font-medium">
        <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary-strong">
          {t('schemaAgent.intro.new')}
        </Badge>
        {t(slide.labelKey)}
      </div>
      <h2 className="max-w-xl text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
        {t(`${slide.contentKey}.title`)}
      </h2>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
        {t(`${slide.contentKey}.description`)}
      </p>
      <ul className="mt-4 grid gap-2.5 pb-1 text-xs text-muted-foreground sm:mt-6 sm:grid-cols-3 sm:gap-5 sm:pb-0">
        {slide.benefits.map(({ key, icon: Icon }) => (
          <li key={key} className="flex items-center gap-2.5">
            <Icon className="size-4 shrink-0 text-primary-strong" aria-hidden />
            {t(`${slide.contentKey}.benefits.${key}`)}
          </li>
        ))}
      </ul>
    </div>
  )
}

function CarouselSlide({
  slide,
  carousel,
  progress,
}: {
  slide: FeatureSlide
  carousel: ReturnType<typeof useFeatureCarousel>
  progress: string
}) {
  const { t } = useTranslation()
  return (
    <div
      ref={element => {
        if (element) element.scrollTop = 0
      }}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      role="group"
      aria-roledescription={t('whatsNew.slide')}
      aria-label={`${progress}: ${t(slide.labelKey)}`}
      onTouchStart={carousel.onTouchStart}
      onTouchEnd={carousel.onTouchEnd}
      onTouchCancel={carousel.onTouchCancel}
    >
      <div
        key={slide.id}
        className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-right-2 motion-safe:duration-300"
      >
        <div className="border-b border-border/60 bg-muted/30 px-3 sm:px-8 [&>svg]:max-h-[28dvh]">
          <slide.Illustration />
        </div>
        <SlideBody slide={slide} />
      </div>
    </div>
  )
}

export function FeatureCarousel({
  slides = featureSlides,
  onSlideViewed,
}: {
  slides?: readonly FeatureSlide[]
  onSlideViewed?: (featureId: string) => void
}) {
  const { t } = useTranslation()
  const carousel = useFeatureCarousel(slides.length)
  const slide = slides[carousel.index]
  useEffect(() => {
    if (slide) onSlideViewed?.(slide.id)
  }, [onSlideViewed, slide])
  if (!slide) return null

  const progress = t('whatsNew.progress', {
    current: carousel.index + 1,
    total: slides.length,
  })
  return (
    <section
      className="flex min-h-0 flex-1 flex-col"
      aria-label={t('whatsNew.title')}
      aria-roledescription={t('whatsNew.carousel')}
      onKeyDown={carousel.onKeyDown}
    >
      <TourHeading progress={progress} />
      <CarouselSlide slide={slide} carousel={carousel} progress={progress} />
      <p className="sr-only" role="status" aria-atomic="true">
        {progress}: {t(slide.labelKey)}
      </p>
      <FeatureCarouselNavigation
        slides={slides}
        index={carousel.index}
        onSelect={carousel.select}
      />
    </section>
  )
}
